import { useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import type { ApplePayMessage, ApplePayRedirectData } from '@/types/applePay'

interface UseApplePayRedirectOptions {
  onSuccess?: () => void
  onError?: (error: string) => void
  onCancel?: () => void
}

export const useApplePayRedirect = (options?: UseApplePayRedirectOptions) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const popupRef = useRef<Window | null>(null)

  // Build Apple Pay checkout URL
  const buildApplePayUrl = useCallback(
    (paymentData: ApplePayRedirectData): string => {
      const params = new URLSearchParams({
        token: paymentData.paymentSessionToken,
        secret: paymentData.paymentSessionSecret,
        transactionId: paymentData.transactionId,
        orderId: paymentData.orderId,
        amount: paymentData.amount,
        currency: paymentData.currency,
      })

      return `${window.location.origin}/apple-pay-checkout?${params.toString()}`
    },
    [],
  )

  // Handle messages from Apple Pay popup
  const handleMessage = useCallback(
    async (event: MessageEvent<ApplePayMessage>) => {
      // Verify origin for security
      if (event.origin !== window.location.origin) {
        return
      }

      // Handle different message types
      switch (event.data.type) {
        case 'APPLE_PAY_SUCCESS': {
          // Call success callback if provided
          options?.onSuccess?.()

          // Show success toast
          toast.success(
            t('messages:success.paymentCompleted', 'Payment successful!'),
          )

          // Invalidate queries to refresh data
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: ['transactions'],
              refetchType: 'all',
            }),
            queryClient.invalidateQueries({
              queryKey: ['wallet'],
              refetchType: 'all',
            }),
            queryClient.invalidateQueries({
              queryKey: ['payment-requests/history'],
              refetchType: 'all',
            }),
          ])

          // Navigate to activity screen
          setTimeout(() => {
            navigate('/activity', { replace: true })
          }, 500)

          break
        }

        case 'APPLE_PAY_ERROR': {
          // Call error callback if provided
          options?.onError?.(event.data.error)

          // Show error toast
          toast.error(
            event.data.error ||
              t('common:paymentFailed', 'Payment failed. Please try again.'),
          )

          break
        }

        case 'APPLE_PAY_CANCELLED': {
          // Call cancel callback if provided
          options?.onCancel?.()

          // Show cancelled toast
          toast.error(t('common:paymentCancelled', 'Payment cancelled'))

          break
        }

        default:
          break
      }
    },
    [navigate, queryClient, options, t],
  )

  // Setup message listener
  useEffect(() => {
    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [handleMessage])

  // Open Apple Pay in Safari/new window
  const openApplePayCheckout = useCallback(
    (paymentData: ApplePayRedirectData) => {
      const url = buildApplePayUrl(paymentData)

      // Open in new window/tab
      // This will trigger Safari to open on iOS devices
      const popup = window.open(url, '_blank', 'noopener,noreferrer')

      if (!popup) {
        toast.error(
          t('common:popupBlocked', 'Please allow popups to use Apple Pay'),
        )
        return
      }

      popupRef.current = popup
    },
    [buildApplePayUrl, t],
  )

  return {
    openApplePayCheckout,
    buildApplePayUrl,
  }
}

export default useApplePayRedirect
